import { T } from "@/utils/i18n";
/**
 * Permissões de exibição que vêm no `/iniciar` (DataContent) e valem para MAIS DE UMA
 * tela. Ficam aqui para não existirem em cópias: uma regra de permissão repetida em
 * quatro arquivos diverge no dia em que alguém ajusta só um deles — e o sintoma é o
 * pior possível, um pedaço da funcionalidade aparecendo onde o cliente desligou.
 */

/**
 * O posto do colaborador libera o Espelho de Ponto?
 *
 * Origem: `tab_adm_clientes.permite_espelho_ponto`, o switch "Espelho de Ponto" do
 * cadastro de cliente no TirvuAdm. Governa TUDO que deriva do espelho — o item de menu,
 * o atalho da Home, o card de Banco de Horas (mesmos dados, mesma tela de destino) e o
 * passo do tour que aponta para ele.
 *
 * Lê a raiz e também `done`: dependendo de quem monta o `userData`, o DataContent chega
 * cru ou embrulhado na resposta da API. Fecha-se por PADRÃO — sem a flag, não mostra:
 * é dado de folha de ponto, e exibir a mais é pior do que exibir a menos.
 */
export const permiteEspelhoPonto = (userData: any): boolean =>
  String(
    userData?.permite_espelho_ponto ??
      userData?.done?.permite_espelho_ponto ??
      "",
  )
    .trim()
    .toUpperCase() === "S";

/** Valores que o backend/legado usa para "não". Só estes bloqueiam. */
const NEGATIVOS = ["N", "NAO", "NÃO", "FALSE", "0"];

/**
 * O colaborador pode registrar ponto pelo aplicativo?
 *
 * Origem: `permitePonto` do /iniciar, que o t-credentials calcula como
 * `IF(registra_ponto = 'N' OR permite_mobile = 'N', 'N', 'S')` — ou seja, some do app
 * tanto quem não registra ponto quanto quem registra só fora do mobile.
 *
 * ⚠️ Abre por PADRÃO, ao contrário do espelho: ausente, vazio ou desconhecido = LIBERA.
 * Registrar ponto é a função central do app e a regra de ouro é que nada pode travar
 * quem está registrando — um campo que não chegou (backend antigo, payload cortado,
 * cache de uma versão anterior) não pode impedir a batida de ninguém. Só o "não"
 * EXPLÍCITO bloqueia, mesmo idioma do `permite_livro_ocorrencia` na BottomBar.
 *
 * Procura na raiz, em `done` e em `colaborador` porque o `userData` chega ora cru, ora
 * embrulhado na resposta da API, dependendo de quem o montou.
 */
export const permiteRegistrarPonto = (userData: any): boolean => {
  const fontes = [userData, userData?.done, userData?.colaborador];
  for (const fonte of fontes) {
    for (const chave of ["permitePonto", "permite_ponto"]) {
      const bruto = String(fonte?.[chave] ?? "")
        .trim()
        .toUpperCase();
      if (!bruto) continue; // vazio não decide nada — segue procurando
      return !NEGATIVOS.includes(bruto);
    }
  }
  return true; // não veio → não bloqueia
};

/**
 * O colaborador registra ponto? (`registra_ponto` de `tab_adm_colaboradores`)
 *
 * NÃO confundir com {@link permiteRegistrarPonto}: aquele é `registra_ponto` E
 * `permite_mobile`, ou seja, "bate ponto POR AQUI". Este é só a primeira metade —
 * "bate ponto, em qualquer canal".
 *
 * A diferença importa: quem registra ponto no relógio da portaria, mas com o mobile
 * bloqueado, continua tendo histórico de batidas e justificativas para consultar no
 * app. Já quem não registra ponto de forma nenhuma não tem o que consultar.
 *
 * Abre por PADRÃO, pelo mesmo motivo do permiteRegistrarPonto: campo ausente não pode
 * esconder o histórico de ninguém.
 */
export const registraPonto = (userData: any): boolean => {
  const fontes = [userData, userData?.done, userData?.colaborador];
  for (const fonte of fontes) {
    for (const chave of ["registraPonto", "registra_ponto"]) {
      const bruto = String(fonte?.[chave] ?? "")
        .trim()
        .toUpperCase();
      if (!bruto) continue;
      return !NEGATIVOS.includes(bruto);
    }
  }
  return true;
};

/**
 * A Home deve oferecer o espelho de ponto?
 *
 * Governa as DUAS portas de entrada do espelho na Home — o card de Banco de Horas e o
 * atalho rápido "Espelho" —, além do esqueleto de carga, dos divisores e dos passos do
 * tour que apontam para eles. O card tem uma condição a mais
 * ({@link mostraBancoHorasNaHome}): some com Tratamento de Horas NENHUM. Regra composta e nomeada aqui, e não repetida em cada
 * ponto: são muitos lugares e todos precisam concordar, senão sobra um caminho aberto
 * para uma tela que o cliente desligou.
 *
 * Duas condições, por motivos diferentes:
 *
 * 1. O posto libera o espelho de ponto (switch "Espelho de Ponto" do cadastro de
 *    cliente no TirvuAdm) — é o mesmo interruptor do item de menu.
 * 2. O colaborador registra ponto pelo app — sem batida não há espelho que preste:
 *    o saldo fica zerado (ou preenchido por lançamento de outra origem) e só gera
 *    dúvida em quem nunca vai bater ponto por aqui.
 */
export const mostraEspelhoNaHome = (userData: any): boolean =>
  permiteEspelhoPonto(userData) && permiteRegistrarPonto(userData);

/**
 * O "Tratamento de Horas" do colaborador: 'S', 'F', 'N' — ou '' quando não veio.
 *
 * Origem: `acumula_banco_horas` da jornada ativa (`userData.jornada[0]`, set 3 do
 * `/iniciar`): [S] Banco de Horas, [F] Folha de Pagamento, [N] Nenhum — o select
 * "Tratamento de Horas" do cadastro de jornada no TirvuAdm. O t-credentials já manda o
 * valor EFETIVO: a exceção do colaborador (`cfg_acumula_banco_horas`) quando existe,
 * senão o da jornada — a mesma regra da folha.
 *
 * Quem usa trata o '' como antes de existir a regra (banco de horas): jornada ausente
 * (cache antigo, backend que não mandou) não muda nada na tela.
 */
export const tratamentoHoras = (userData: any): "S" | "F" | "N" | "" => {
  const jornada = userData?.jornada ?? userData?.done?.jornada;
  const atual = Array.isArray(jornada) ? jornada[0] : jornada;
  const valor = String(atual?.acumula_banco_horas ?? "")
    .trim()
    .toUpperCase();
  return valor === "S" || valor === "F" || valor === "N" ? valor : "";
};

/**
 * O "Tratamento de Horas" é NENHUM? As horas são calculadas no mês mas não acumulam nem
 * vão para a folha: não existe banco de horas para mostrar. Some o card de Banco de Horas
 * da Home e o do Espelho; o espelho em si (os dias calculados) continua. Só o 'N'
 * explícito esconde.
 */
export const tratamentoHorasNenhum = (userData: any): boolean =>
  tratamentoHoras(userData) === "N";

/**
 * Como a tela chama o saldo de horas. "Banco de horas" só quando há banco: com Folha de
 * Pagamento ('F') as horas do mês vão para a folha e não acumulam, então o nome é
 * genérico (pedido de 08/10/2026). Fonte única dos rótulos — card e tour da Home, gráfico
 * e rodapé dos dias do Espelho. Trocar o termo é aqui.
 */
/**
 * Mostrar o "Acumulado" das horas (pílula do card da Home e caixa do topo do Espelho)?
 * Com Folha de Pagamento ('F') as horas não passam para os meses seguintes — não há o
 * que acumular, e o número só confundiria (pedido de 08/10/2026). NENHUM não entra aqui:
 * na Home o card inteiro já some ({@link mostraBancoHorasNaHome}).
 */
export const mostraAcumuladoHoras = (userData: any): boolean =>
  tratamentoHoras(userData) !== "F";

export const rotulosSaldoHoras = (
  userData: any,
): { titulo: string; grafico: string; dia: string } =>
  tratamentoHoras(userData) === "F"
    ? { titulo: T("Horas do mês"), grafico: T("Horas Mensais"), dia: T("Saldo") }
    : {
        titulo: T("Banco de horas"),
        grafico: T("Banco de Horas Mensais"),
        dia: T("Banco"),
      };

/**
 * A Home deve mostrar o card de Banco de Horas? As portas do espelho
 * ({@link mostraEspelhoNaHome}) E um tratamento de horas que tenha banco
 * ({@link tratamentoHorasNenhum}). Governa o card, o esqueleto de carga, o divisor e o
 * passo do tour. O atalho "Espelho" NÃO entra aqui: o espelho continua existindo para
 * quem é NENHUM.
 */
export const mostraBancoHorasNaHome = (userData: any): boolean =>
  mostraEspelhoNaHome(userData) && !tratamentoHorasNenhum(userData);

/**
 * O módulo de Livro de Ocorrência está disponível para este colaborador?
 *
 * Duas portas, ambas precisam estar abertas:
 * 1. `tem_acesso_livro` — a SEÇÃO existe no tenant (`tab_adm_secoes`, `livro-ocorrencia`).
 * 2. `permite_livro_ocorrencia` — o POSTO libera (`tab_adm_clientes.livro_ocorrencia`);
 *    bloqueia só no `'N'` explícito, para não regredir antes de um redeploy.
 *
 * ⚠️ Usado como PRÉ-CONDIÇÃO da trava de saída no registro de ponto. Sem ela existe um
 * beco sem saída: colaborador que ficou com um plantão antigo aberto e depois teve o
 * módulo desligado não conseguiria bater a saída NEM chegar à tela para fechar o livro —
 * o item do menu não existe mais para ele.
 *
 * Fecha por padrão de propósito: se as flags não chegaram, o módulo é tratado como
 * indisponível e a trava não bloqueia ninguém.
 */
export const temLivroOcorrencia = (userData: any): boolean => {
  const temSecao = Boolean(
    userData?.tem_acesso_livro ?? userData?.done?.tem_acesso_livro,
  );
  const postoLibera =
    String(
      userData?.permite_livro_ocorrencia ??
        userData?.done?.permite_livro_ocorrencia ??
        "",
    )
      .trim()
      .toUpperCase() !== "N";
  return temSecao && postoLibera;
};

/**
 * O MENU principal deve oferecer o espelho de ponto?
 *
 * Vizinho de {@link mostraEspelhoNaHome} de propósito: as duas regras estão lado a lado
 * porque diferem numa condição só, e a diferença é fácil de aplicar errado.
 *
 * Aqui a segunda condição é {@link registraPonto} (bate ponto em QUALQUER canal), não
 * {@link permiteRegistrarPonto} (bate ponto POR AQUI). Quem registra no relógio da
 * portaria com o mobile bloqueado tem espelho de verdade para consultar — o menu abre,
 * pelo mesmo motivo que o histórico abre.
 *
 * ⚠️ Consequência: esse colaborador vê o espelho pelo MENU e não vê o card de Banco de
 * Horas nem o atalho na Home, que usam a regra mais restritiva. É assimetria conhecida,
 * não descuido; se algum dia decidirem alinhar, o lugar é aqui.
 */
export const mostraEspelhoNoMenu = (userData: any): boolean =>
  permiteEspelhoPonto(userData) && registraPonto(userData);
